// /home/ubuntu/avaai_dashboard/frontend/src/pages/Subscribe.tsx

import React, { useContext, useEffect, useMemo, useState } from "react";
import { getAuthToken } from "../utils/auth";
import { Link } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";

const API_BASE = "";

function PaymentBadge({
  styles,
}: {
  styles: Record<string, React.CSSProperties>;
}) {
  return (
    <div style={styles.stripeWrap}>
      <svg width="18" height="18" viewBox="0 0 32 32" aria-hidden="true">
        <rect x="2" y="2" width="28" height="28" rx="8" fill="#635BFF" />
      </svg>
      <span style={styles.stripeText}>Apple In-App Purchase</span>
    </div>
  );
}

export default function Subscribe() {
  const { themeColors } = useContext(ThemeContext);
  const styles = useMemo(() => makeStyles(themeColors), [themeColors]);

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const stored = localStorage.getItem("ava_user");
    if (!stored) return;

    try {
      const parsed = JSON.parse(stored);
      if (parsed?.email) setEmail(parsed.email);
    } catch {
      // ignore malformed local storage
    }
  }, []);

  useEffect(() => {
    const reset = () => {
      setLoading(false);
      setMessage("");
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") reset();
    };

    reset();

    window.addEventListener("pageshow", reset);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pageshow", reset);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  async function handleSubscribe() {
    setMessage(
      "AVAAIO paid plans are not available before the official release."
    );
  }

  function goBack() {
    window.location.href = "/login";
  }

  return (
    <div style={styles.page}>
      <div style={styles.bgGlowTop} />
      <div style={styles.bgGlowBottom} />

      <div style={styles.card}>
        <div style={styles.brandLine}>AVAAIO™</div>

        <h1 style={styles.title}>Pro Plans Coming Soon</h1>

        <p style={styles.subtitle}>
          Every approved account remains on the
          AVAAIO Standard plan during preview.
          <br />
          Paid subscriptions will open at release.
        </p>

        <div style={styles.inputWrap}>
          <input
            value={email}
            readOnly
            disabled
            placeholder="Your account email is used automatically after login"
            style={styles.input}
          />
        </div>

        <button
          onClick={handleSubscribe}
          style={{
            ...styles.button,
            ...(loading ? styles.buttonDisabled : {}),
          }}
          disabled={true}
        >
          {"Pro Plans Available at Release"}
        </button>

        <div style={styles.footer}>
          <PaymentBadge styles={styles} />

          <button onClick={goBack} style={styles.backButton}>
            ← Continue with Free / Back to Login
          </button>
        </div>

        {message ? <p style={styles.error}>{message}</p> : null}

        <div style={styles.legalFooter}>
          <div style={styles.legalPowered}>Ava is powered by AVAAIO™</div>
          <div style={styles.legalTech}>Built on American Global™ AI Technology</div>
          <div style={styles.legalCopy}>
            © 2026 American Globalo, LLC. All rights reserved.
          </div>
          <div style={styles.legalTrademark}>AVAAIO™ is a trademark of American Globalo, LLC.</div>
          <div style={styles.legalLinks}>
            <Link to="/privacy-policy" style={styles.legalLink}>
              Privacy Policy
            </Link>
            <span style={styles.legalSeparator}> | </span>
            <Link to="/terms-and-conditions" style={styles.legalLink}>
              Terms &amp; Conditions
            </Link>
            <span style={styles.legalSeparator}> | </span>
            <Link to="/ai-disclaimer" style={styles.legalLink}>
              AI Disclaimer
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const makeStyles = (themeColors: any): Record<string, React.CSSProperties> => ({
  page: {
    minHeight: "100vh",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    background: themeColors.bg,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "clamp(18px, 3vw, 32px)",
    boxSizing: "border-box",
    fontFamily:
      'Inter, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },

  bgGlowTop: {
    position: "absolute",
    top: "-10%",
    left: "50%",
    transform: "translateX(-50%)",
    width: "min(66vw, 760px)",
    height: "min(66vw, 760px)",
    borderRadius: "50%",
    background: `radial-gradient(circle, ${themeColors.accent}1f 0%, ${themeColors.accent}0a 36%, transparent 72%)`,
    filter: "blur(28px)",
    pointerEvents: "none",
  },

  bgGlowBottom: {
    position: "absolute",
    bottom: "-18%",
    left: "50%",
    transform: "translateX(-50%)",
    width: "min(58vw, 640px)",
    height: "min(58vw, 640px)",
    borderRadius: "50%",
    background: `radial-gradient(circle, ${themeColors.accent2}14 0%, ${themeColors.accent2}05 40%, transparent 72%)`,
    filter: "blur(28px)",
    pointerEvents: "none",
  },

  card: {
    position: "relative",
    zIndex: 1,
    width: "min(100%, 520px)",
    borderRadius: "24px",
    border: `1px solid ${themeColors.border}`,
    background: themeColors.panel,
    boxShadow: themeColors.glow,
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    padding: "30px 24px 22px",
    boxSizing: "border-box",
    textAlign: "center",
  },

  brandLine: {
    textAlign: "center",
    fontSize: "0.88rem",
    letterSpacing: "0.32em",
    textTransform: "uppercase",
    color: themeColors.accent2,
    marginBottom: "12px",
    fontWeight: 800,
    fontFamily:
      '"Orbitron", Inter, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    textShadow: themeColors.glow,
  },

  title: {
    margin: "0 0 10px",
    fontSize: "clamp(2rem, 4vw, 2.7rem)",
    lineHeight: 1.08,
    color: themeColors.text,
    fontWeight: 900,
    letterSpacing: "-0.02em",
    fontFamily:
      '"Orbitron", Inter, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    textShadow: themeColors.glow,
  },

  subtitle: {
    margin: "0 0 24px",
    fontSize: "0.98rem",
    lineHeight: 1.6,
    color: themeColors.mutedText,
    fontWeight: 500,
  },

  inputWrap: {
    marginBottom: "14px",
  },

  input: {
    width: "100%",
    minHeight: "54px",
    padding: "0 16px",
    borderRadius: "16px",
    border: `1px solid ${themeColors.border}`,
    background: themeColors.cardBg,
    color: themeColors.text,
    boxSizing: "border-box",
    outline: "none",
    fontSize: "0.98rem",
    fontWeight: 500,
    letterSpacing: "0.01em",
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.02)",
  },

  button: {
    width: "100%",
    minHeight: "54px",
    borderRadius: "16px",
    border: "none",
    background: `linear-gradient(90deg, ${themeColors.accent} 0%, ${themeColors.accent2} 55%, ${themeColors.text} 100%)`,
    color: themeColors.bg,
    fontSize: "1rem",
    fontWeight: 900,
    letterSpacing: "0.04em",
    cursor: "pointer",
    boxShadow: themeColors.glow,
  },

  buttonDisabled: {
    opacity: 0.62,
    cursor: "not-allowed",
    boxShadow: "none",
  },

  footer: {
    marginTop: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
  },

  stripeWrap: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
    color: themeColors.mutedText,
    fontWeight: 600,
  },

  stripeText: {
    fontSize: "12px",
    letterSpacing: "0.02em",
  },

  backButton: {
    width: "100%",
    minHeight: "48px",
    marginTop: "2px",
    borderRadius: "14px",
    border: `1px solid ${themeColors.border}`,
    background: themeColors.buttonBg,
    color: themeColors.accent2,
    fontSize: "0.95rem",
    fontWeight: 700,
    letterSpacing: "0.01em",
    cursor: "pointer",
  },

  error: {
    marginTop: "14px",
    color: "#ff9a9a",
    fontSize: "0.9rem",
    lineHeight: 1.5,
    fontWeight: 600,
  },

  legalFooter: {
    marginTop: "18px",
    textAlign: "center",
    fontSize: "0.72rem",
    lineHeight: 1.5,
    color: themeColors.mutedText,
  },

  legalPowered: {
    marginBottom: "4px",
    fontWeight: 600,
  },

  legalTech: {
    fontSize: "11px",
    color: themeColors.mutedText,
    marginTop: "6px",
  },
  legalTrademark: {
    fontSize: "10px",
    color: themeColors.mutedText,
    marginTop: "4px",
  },
  legalCopy: {
    marginBottom: "6px",
  },

  legalLinks: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
  },

  legalLink: {
    color: themeColors.accent2,
    textDecoration: "none",
    fontWeight: 700,
  },

  legalSeparator: {
    color: themeColors.border,
    padding: "0 6px",
  },
});
